import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, 
  ChevronLeft, 
  CheckCircle2,
  Globe,
  Calendar,
  Building,
  Users,
  Mail,
  Loader2,
  DollarSign,
  Settings,
  UserCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to publish height to parent iframe
const publishHeight = () => {
  const height = document.documentElement.scrollHeight;
  window.parent.postMessage({ type: 'resize', height }, '*');
};

// Helper to notify parent to scroll to top
const scrollParentToTop = () => {
  window.parent.postMessage({ type: 'scrollToTop' }, '*');
};

// Form options
const businessTypes = [
  { id: 'agency', label: 'Insurance Agency', description: 'Multiple agents under one organization' },
  { id: 'independent', label: 'Independent Agent', description: 'Solo practitioner or small team' },
  { id: 'bga', label: 'BGA (Broker General Agency)', description: 'Wholesale distribution organization' },
  { id: 'other', label: 'Other', description: 'Another type of business or organization' },
];

const agentCountOptions = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 agents' },
  { value: '6-10', label: '6-10 agents' },
  { value: '11-25', label: '11-25 agents' },
  { value: '26-50', label: '26-50 agents' },
  { value: '50+', label: '50+ agents' },
];

const marketingPlatforms = [
  { id: 'facebook', label: 'Facebook/Meta' },
  { id: 'google', label: 'Google Ads' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'seo', label: 'SEO/Organic' },
  { id: 'email', label: 'Email Marketing' },
  { id: 'none', label: 'No experience yet' },
];

const budgetOptions = [
  { value: 'under-2500', label: 'Under $2,500/month' },
  { value: '2500-5000', label: '$2,500 - $5,000/month' },
  { value: '5000-10000', label: '$5,000 - $10,000/month' },
  { value: '10000-25000', label: '$10,000 - $25,000/month' },
  { value: '25000+', label: '$25,000+/month' },
];

const crmOptions = [
  { value: 'none', label: 'No CRM currently' },
  { value: 'gohighlevel', label: 'GoHighLevel' },
  { value: 'salesforce', label: 'Salesforce' },
  { value: 'hubspot', label: 'HubSpot' },
  { value: 'agencybloc', label: 'AgencyBloc' },
  { value: 'radiusbob', label: 'Radiusbob' },
  { value: 'other', label: 'Other' },
];

// Loading skeleton component
const FormSkeleton = () => (
  <div className="min-h-fit bg-transparent py-8 px-4 animate-pulse">
    <div className="max-w-full w-full" style={{ maxWidth: '100%' }}>
      {/* Header skeleton */}
      <div className="text-center mb-8">
        <div className="h-8 bg-slate-200 rounded-lg w-64 mx-auto mb-3"></div>
        <div className="h-4 bg-slate-200 rounded w-80 mx-auto"></div>
      </div>

      {/* Progress bar skeleton */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          <div className="h-4 bg-slate-200 rounded w-20"></div>
          <div className="h-4 bg-slate-200 rounded w-24"></div>
        </div>
        <div className="h-2 bg-slate-200 rounded-full"></div>
      </div>

      {/* Form card skeleton */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
        {/* Header with icon */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-slate-200"></div>
          <div className="flex-1">
            <div className="h-6 bg-slate-200 rounded w-48 mb-2"></div>
            <div className="h-4 bg-slate-200 rounded w-36"></div>
          </div>
        </div>
        
        {/* Options skeleton */}
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-4 rounded-xl border-2 border-slate-200">
              <div className="h-5 bg-slate-200 rounded w-32 mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-56"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation button skeleton */}
      <div className="mt-6 flex justify-end">
        <div className="h-12 bg-slate-200 rounded-xl w-32"></div>
      </div>
    </div>
  </div>
);

export const MarketingFormWidget = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  
  // Set up iframe height communication with parent
  useEffect(() => {
    // Suppress body scrollbar and set transparent background for iframe embedding
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.background = 'transparent';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.background = 'transparent';

    // Short delay to ensure DOM is ready, then show content and publish height
    const initTimeout = setTimeout(() => {
      setIsLoading(false);
      requestAnimationFrame(() => {
        publishHeight();
      });
    }, 150);

    // Observe body resize (step transitions, validation messages, etc.)
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(publishHeight);
      resizeObserver.observe(document.body);
    }

    // Also publish on window resize for safety
    window.addEventListener('resize', publishHeight);

    return () => {
      clearTimeout(initTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', publishHeight);
    };
  }, []);

  // Publish height whenever step or submission state changes
  useEffect(() => {
    // Small delay to allow animations to complete
    const timeout = setTimeout(publishHeight, 100);
    return () => clearTimeout(timeout);
  }, [currentStep, submissionSuccess, isLoading]);

  // Form state
  const [businessType, setBusinessType] = useState('');
  const [agentCount, setAgentCount] = useState('');
  const [hasWebsite, setHasWebsite] = useState(null);
  const [website, setWebsite] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [marketingExperience, setMarketingExperience] = useState([]);
  const [budget, setBudget] = useState('');
  const [currentCrm, setCurrentCrm] = useState('');
  const [openToGoHighLevel, setOpenToGoHighLevel] = useState(null);
  
  // Contact form state
  const [contactName, setContactName] = useState('');
  const [contactCompany, setContactCompany] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactPhoneError, setContactPhoneError] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactEmailError, setContactEmailError] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');

  const totalSteps = 5;

  // Validation functions
  const validateWebsite = (url) => {
    if (!url) return true;
    const pattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    return pattern.test(url);
  };

  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };

  const validateEmail = (email) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setContactPhone(formatted);
    if (formatted && !validatePhone(formatted)) {
      setContactPhoneError('Please enter a valid 10-digit phone number');
    } else {
      setContactPhoneError('');
    }
  };

  const handleEmailChange = (e) => {
    setContactEmail(e.target.value);
    if (e.target.value && !validateEmail(e.target.value)) {
      setContactEmailError('Please enter a valid email address');
    } else {
      setContactEmailError('');
    }
  };

  const handleWebsiteChange = (e) => {
    setWebsite(e.target.value);
    if (e.target.value && !validateWebsite(e.target.value)) {
      setWebsiteError('Please enter a valid website URL');
    } else {
      setWebsiteError('');
    }
  };

  const togglePlatform = (platformId) => {
    setMarketingExperience(prev => 
      prev.includes(platformId) 
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  // Handle step navigation with scroll to top for parent
  const goToNextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
    scrollParentToTop();
  }, []);

  const goToPrevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
    scrollParentToTop();
  }, []);

  // Step validation
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return businessType !== '';
      case 2:
        if (hasWebsite === null || agentCount === '') return false;
        if (hasWebsite && (!website || websiteError)) return false;
        return true;
      case 3:
        return marketingExperience.length > 0 && budget !== '';
      case 4:
        return currentCrm !== '' && openToGoHighLevel !== null;
      case 5:
        return contactName !== '' && 
               contactCompany !== '' && 
               contactPhone !== '' && 
               !contactPhoneError &&
               contactEmail !== '' && 
               !contactEmailError;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    
    setIsSubmitting(true);
    
    try {
      const formData = {
        business_type: businessType,
        agent_count: agentCount,
        has_website: hasWebsite,
        website: website || null,
        current_website: website || null,
        marketing_experience: marketingExperience,
        budget: budget,
        current_crm: currentCrm,
        open_to_gohighlevel: openToGoHighLevel,
        name: contactName,
        company_name: contactCompany,
        phone: contactPhone.replace(/\D/g, ''),
        email: contactEmail,
        additional_details: additionalDetails || null,
      };

      await axios.post(`${API}/marketing-inquiry`, formData);
      setSubmissionSuccess(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading skeleton while initializing
  if (isLoading) {
    return <FormSkeleton />;
  }

  if (submissionSuccess) {
    return (
      <div className="min-h-fit bg-transparent flex items-center justify-center p-4">
        <div className="max-w-full w-full text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-[#10c0a5] rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle2 className="w-10 h-10 text-white" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Thanks for Your Interest!
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-600 mb-8"
          >
            We've received your inquiry and will review your information shortly. 
            If we think we're a good fit, one of our team members will reach out within 1-2 business days.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#e6faf7]/50 rounded-xl p-6 mb-8"
          >
            <h2 className="font-semibold text-slate-900 mb-3">What happens next?</h2>
            <ul className="text-sm text-slate-600 space-y-2 text-left max-w-md mx-auto">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                <span>We'll review your marketing goals and current setup</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                <span>If we're a good match, we'll schedule a discovery call</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                <span>We'll create a custom marketing strategy for your agency</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="https://www.quoteplicity.com/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#10c0a5] hover:bg-[#0ea892] text-white font-medium rounded-xl transition-colors"
            >
              <Globe className="w-5 h-5" />
              Visit Quoteplicity
            </a>
            <a
              href="https://www.quoteplicity.com/lp-book-call"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#10c0a5] text-[#10c0a5] hover:bg-[#e6faf7] font-medium rounded-xl transition-colors"
            >
              <Calendar className="w-5 h-5" />
              Book a Call
            </a>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-fit bg-transparent py-8 px-4">
      <div className="max-w-full w-full" style={{ maxWidth: '100%' }}>
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            See If We're a Good Fit
          </h1>
          <p className="text-slate-600">Answer a few questions so we can learn about your marketing needs</p>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-slate-500 mb-2">
            <span>Step {currentStep} of {totalSteps}</span>
            <span>{Math.round((currentStep / totalSteps) * 100)}% complete</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-[#10c0a5]"
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Form Steps */}
        <AnimatePresence mode="wait">
          {/* Step 1: Business Type */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                  <Building className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Tell us about your business
                  </h2>
                  <p className="text-slate-600">What type of organization are you?</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {businessTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setBusinessType(type.id)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      businessType === type.id
                        ? 'border-[#10c0a5] bg-[#e6faf7]'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`business-type-${type.id}`}
                  >
                    <div className="font-medium text-slate-900">{type.label}</div>
                    <div className="text-sm text-slate-500">{type.description}</div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Team & Website */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Your team & online presence
                  </h2>
                  <p className="text-slate-600">Help us understand your current setup</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">How many agents are in your organization?</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {agentCountOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setAgentCount(option.value)}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          agentCount === option.value
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 hover:border-slate-300 text-slate-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Do you currently have a website?</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setHasWebsite(true)}
                      className={`flex-1 p-3 rounded-lg border-2 font-medium transition-all ${
                        hasWebsite === true
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => { setHasWebsite(false); setWebsite(''); setWebsiteError(''); }}
                      className={`flex-1 p-3 rounded-lg border-2 font-medium transition-all ${
                        hasWebsite === false
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
                
                {hasWebsite && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                  >
                    <label className="block text-sm font-medium text-slate-700 mb-2">What's your website URL?</label>
                    <input
                      type="url"
                      value={website}
                      onChange={handleWebsiteChange}
                      placeholder="https://yourwebsite.com"
                      className={`w-full p-3 rounded-lg border-2 ${websiteError ? 'border-red-300' : 'border-slate-200'} focus:border-[#10c0a5] focus:outline-none transition-colors`}
                    />
                    {websiteError && <p className="mt-1 text-sm text-red-500">{websiteError}</p>}
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3: Marketing Experience & Budget */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                  <DollarSign className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Marketing experience & budget
                  </h2>
                  <p className="text-slate-600">Let us know about your marketing background</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Which platforms have you used for marketing? (Select all that apply)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {marketingPlatforms.map((platform) => (
                      <button
                        key={platform.id}
                        onClick={() => togglePlatform(platform.id)}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          marketingExperience.includes(platform.id)
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 hover:border-slate-300 text-slate-700'
                        }`}
                      >
                        {platform.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">What's your monthly marketing budget?</label>
                  <div className="space-y-2">
                    {budgetOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setBudget(option.value)}
                        className={`w-full p-3 rounded-lg border-2 text-left font-medium transition-all ${
                          budget === option.value
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 hover:border-slate-300 text-slate-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: CRM & GoHighLevel */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                  <Settings className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Your current systems
                  </h2>
                  <p className="text-slate-600">Tell us about your CRM and technology stack</p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">What CRM are you currently using?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {crmOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setCurrentCrm(option.value)}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          currentCrm === option.value
                            ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                            : 'border-slate-200 hover:border-slate-300 text-slate-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Are you open to using GoHighLevel for CRM and automation?</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setOpenToGoHighLevel(true)}
                      className={`flex-1 p-3 rounded-lg border-2 font-medium transition-all ${
                        openToGoHighLevel === true
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      Yes, I'm open to it
                    </button>
                    <button
                      onClick={() => setOpenToGoHighLevel(false)}
                      className={`flex-1 p-3 rounded-lg border-2 font-medium transition-all ${
                        openToGoHighLevel === false
                          ? 'border-[#10c0a5] bg-[#e6faf7] text-[#10c0a5]'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      No, I prefer my current setup
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 5: Contact Info */}
          {currentStep === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10c0a5] to-[#0d9488] flex items-center justify-center shadow-lg shadow-[#10c0a5]/20">
                  <UserCircle className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Your contact information
                  </h2>
                  <p className="text-slate-600">Let us know how to reach you</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Your Name *</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="John Smith"
                    className="w-full p-3 rounded-lg border-2 border-slate-200 focus:border-[#10c0a5] focus:outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company/Agency Name *</label>
                  <input
                    type="text"
                    value={contactCompany}
                    onChange={(e) => setContactCompany(e.target.value)}
                    placeholder="Your Insurance Agency"
                    className="w-full p-3 rounded-lg border-2 border-slate-200 focus:border-[#10c0a5] focus:outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={contactPhone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 555-5555"
                    className={`w-full p-3 rounded-lg border-2 ${contactPhoneError ? 'border-red-300' : 'border-slate-200'} focus:border-[#10c0a5] focus:outline-none transition-colors`}
                  />
                  {contactPhoneError && <p className="mt-1 text-sm text-red-500">{contactPhoneError}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    inputMode="email"
                    value={contactEmail}
                    onChange={handleEmailChange}
                    placeholder="you@example.com"
                    className={`w-full p-3 rounded-lg border-2 ${contactEmailError ? 'border-red-300' : 'border-slate-200'} focus:border-[#10c0a5] focus:outline-none transition-colors`}
                  />
                  {contactEmailError && <p className="mt-1 text-sm text-red-500">{contactEmailError}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Anything else you'd like us to know? (Optional)</label>
                  <textarea
                    value={additionalDetails}
                    onChange={(e) => setAdditionalDetails(e.target.value)}
                    placeholder="What are your biggest marketing challenges? What does success look like for you?"
                    rows={4}
                    className="w-full p-3 rounded-lg border-2 border-slate-200 focus:border-[#10c0a5] focus:outline-none transition-colors resize-none"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="mt-6 flex justify-between">
          {currentStep > 1 ? (
            <button
              onClick={goToPrevStep}
              className="flex items-center gap-2 px-6 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
          ) : (
            <div />
          )}
          
          {currentStep < totalSteps ? (
            <button
              onClick={goToNextStep}
              disabled={!canProceed()}
              className={`flex items-center gap-2 px-8 py-3 rounded-xl font-medium transition-all ${
                canProceed()
                  ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              Continue
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              className={`flex items-center gap-2 px-8 py-3 rounded-xl font-medium transition-all ${
                canProceed() && !isSubmitting
                  ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingFormWidget;
